import { Box, Button, Typography } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import { useEffect, useMemo, useState } from "react";
import { mcaI20Logo } from "../../../Assets";
import { useAuthStore } from "../../../store";
import { getTeamDetails } from "../../../Utils/apiService";
import TeamSection from "./components/TeamSection";

type TeamDivisionTab = "men" | "women";

const resolveDivisionFromTeamType = (teamType?: string | null): TeamDivisionTab => {
  const normalized = String(teamType || "")
    .toLowerCase()
    .trim();

  if (normalized.includes("women") || normalized.includes("female")) {
    return "women";
  }

  return "men";
};

type TeamDetailsProps = {
  showCategoryBreakdown?: boolean;
  showDivisionTabs?: boolean;
  onBackClick?: () => void;
};

const TeamDetails = ({
  showCategoryBreakdown = true,
  showDivisionTabs = false,
  onBackClick,
}: TeamDetailsProps) => {
  const [teamDetails, setTeamDetails] = useState<any>();
  const { teamDetailsTrigger, teamDetailsDivision } = useAuthStore();
  const [localDivision, setLocalDivision] = useState<TeamDivisionTab>("men");
  const activeDivision: TeamDivisionTab = showDivisionTabs
    ? localDivision
    : teamDetailsDivision === "women"
      ? "women"
      : "men";
  const divisionLabel = activeDivision === "women" ? "WOMEN'S" : "MEN'S";

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [teamsRes] = await Promise.all([getTeamDetails()]);

        if (teamsRes?.data?.success) {
          setTeamDetails(teamsRes.data.data);
        }
      } catch (err) {
        console.error("Error fetching data:", err);
      }
    };

    fetchData();
  }, [teamDetailsTrigger]);

  const filteredTeams = useMemo(() => {
    return (teamDetails || []).filter((team: any) => {
      return resolveDivisionFromTeamType(team?.Team_Type__c) === activeDivision;
    });
  }, [teamDetails, activeDivision]);

  const renderInitialLogo = () => (
    <Box
      sx={{
        width: "100%",
        minHeight: "100%",
        background: "transparent",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          gap: { xs: 2, md: "1.2vw" },
          mb: { xs: 4, md: "1.6vw" },
        }}
      >
        <img src={mcaI20Logo} style={{ height: "4.6vw", width: "auto", minHeight: 72 }} alt="" />
        <Typography sx={{ fontSize: { xs: "1.4rem", md: "2.2vw" }, color: "#fff", fontWeight: 800 }}>
          T20 Mumbai League - 2025
        </Typography>
      </Box>
    </Box>
  );

  if (!teamDetails) {
    return renderInitialLogo();
  }

  return (
    <Box
      sx={{
        width: "100%",
        height: "100vh",
        minHeight: "100vh",
        position: "relative",
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        pt: 0,
        pb: { xs: 1.5, md: "1vw" },
      }}
    >
      <Box
        sx={{
          width: "100%",
          height: "95%",
          display: "flex",
          flexDirection: "column",
          pt: 0,
          px: { xs: 2.2, md: "1.2vw" },
          pb: { xs: 2, md: "0.8vw" },
          minHeight: 0,
        }}
      >
        <Box
          sx={{
            border: "1px solid rgba(255, 124, 24, 0.3)",
            background:
              "linear-gradient(90deg, rgba(50,19,5,0.96) 0%, rgba(110,42,2,0.95) 70%, rgba(227,116,16,0.95) 100%)",
            p: { xs: 1.2, md: "0.55vw" },
            position: "relative",
            mb: { xs: 2, md: "0.8vw" },
            "&::after": {
              content: '""',
              position: "absolute",
              left: 0,
              right: 0,
              bottom: -4,
              height: "4px",
              background:
                "linear-gradient(90deg, #e41f4f 0%, #e41f4f 12%, #24c864 12%, #24c864 26%, #1ba8ff 26%, #1ba8ff 40%, #4e5dff 40%, #4e5dff 54%, #ff8b1a 54%, #ff8b1a 67%, #ff3e4d 67%, #ff3e4d 79%, #ffd400 79%, #ffd400 90%, #22d37b 90%, #22d37b 100%)",
            },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography
              sx={{
                color: "#fff",
                fontWeight: 800,
                fontStyle: "italic",
                fontSize: { xs: "1.35rem", md: "2.1vw" },
                lineHeight: 1,
                background:
                  "linear-gradient(90deg, #FF8C1E 0%, #FF3E9A 60%, #FF6B39 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              {`TEAMS PARTICIPATING - ${divisionLabel}`}
            </Typography>
          </Box>
        </Box>
        {showDivisionTabs && (
          <Box
            sx={{
              mt: { xs: 1.1, md: "0.7vw" },
              mb: { xs: 1.4, md: "0.7vw" },
              display: "flex",
              justifyContent: "flex-start",
              alignItems: "center",
              gap: 1,
            }}
          >
            {onBackClick && (
              <IconButton
                onClick={onBackClick}
                sx={{
                  color: "#fff",
                  p: { xs: "5px", md: "0.32vw" },
                  border: "1px solid rgba(255,255,255,0.25)",
                  borderRadius: "8px",
                  bgcolor: "rgba(10,10,12,0.45)",
                  "&:hover": {
                    bgcolor: "rgba(255,255,255,0.12)",
                  },
                }}
              >
                <ArrowBackIosNewIcon sx={{ fontSize: { xs: 16, md: 20 } }} />
              </IconButton>
            )}
            <Button
              variant={activeDivision === "men" ? "contained" : "outlined"}
              onClick={() => setLocalDivision("men")}
              sx={{
                minWidth: { xs: 72, md: 94 },
                height: { xs: 32, md: 36 },
                textTransform: "none",
                fontWeight: 700,
                borderColor: "rgba(255,255,255,0.35)",
                color: "#fff",
                bgcolor:
                  activeDivision === "men"
                    ? "rgba(255,140,30,0.9)"
                    : "transparent",
                "&:hover": {
                  borderColor: "rgba(255,255,255,0.5)",
                  bgcolor:
                    activeDivision === "men"
                      ? "rgba(255,140,30,1)"
                      : "rgba(255,255,255,0.08)",
                },
              }}
            >
              Men
            </Button>
            <Button
              variant={activeDivision === "women" ? "contained" : "outlined"}
              onClick={() => setLocalDivision("women")}
              sx={{
                minWidth: { xs: 84, md: 106 },
                height: { xs: 32, md: 36 },
                textTransform: "none",
                fontWeight: 700,
                borderColor: "rgba(255,255,255,0.35)",
                color: "#fff",
                bgcolor:
                  activeDivision === "women"
                    ? "rgba(255,62,154,0.9)"
                    : "transparent",
                "&:hover": {
                  borderColor: "rgba(255,255,255,0.5)",
                  bgcolor:
                    activeDivision === "women"
                      ? "rgba(255,62,154,1)"
                      : "rgba(255,255,255,0.08)",
                },
              }}
            >
              Women
            </Button>
          </Box>
        )}

        <Box
          sx={{
            background:
              "linear-gradient(180deg, rgba(18, 7, 15, 0.55) 0%, rgba(8, 8, 12, 0.52) 100%)",
            border: "1px solid rgba(255,255,255,0.08)",
            px: { xs: 1.2, md: "0.9vw" },
            py: { xs: 1.6, md: "0.9vw" },
            flex: 1,
          }}
        >
          <TeamSection
            teamDetails={filteredTeams}
            showCategoryBreakdown={showCategoryBreakdown}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default TeamDetails;
